import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import SEO from "../components/SEO";
import axios from "axios";
import CandidatoAvatar from "../components/CandidatoAvatar";
import StateHistoryChart from "../components/StateHistoryChart";
import StateMunicipalitiesMap from "../components/StateMunicipalitiesMap";
import KPICard from "../components/ui/KPICard";
import Card from "../components/ui/Card";
import { TrendingUp, TrendingDown, DollarSign, ArrowLeft, Vote, UserCheck, Stethoscope, MapPin, ClipboardList, Users, BarChart3 } from "lucide-react";

// Hook para detectar tamanho da tela
const useMediaQuery = (query) => {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== "undefined") return window.matchMedia(query).matches;
    return false;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;

    const media = window.matchMedia(query);
    setMatches(media.matches);

    const listener = (event) => setMatches(event.matches);

    // compat
    if (media.addListener) {
      media.addListener(listener);
      return () => media.removeListener(listener);
    }

    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [query]);

  return matches;
};

const StatePage = () => {
  const { stateSigla, year } = useParams();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");

  const [stateData, setStateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polData, setPolData] = useState(null);
  const [showAllPrefeitos, setShowAllPrefeitos] = useState(false);
  const [medData, setMedData] = useState(null);
  const [popData, setPopData] = useState(null);
  const [pibData, setPibData] = useState(null);

  const stateUF = useMemo(() => (stateSigla ? stateSigla.toUpperCase() : ""), [stateSigla]);

  const selectedYear = useMemo(() => {
    const y = parseInt(year, 10);
    return Number.isFinite(y) ? y : 2025;
  }, [year]);

  useEffect(() => {
    let alive = true;

    const fetchStateData = async () => {
      try {
        setLoading(true);

        // Fetch politicos in parallel
        axios.get(`/politicos/estado/${stateUF}`, { params: { ano: 2024 } })
          .then((r) => { if (r.data?.success) setPolData(r.data.data); })
          .catch(() => {});

        axios.get(`/med/estado/${stateUF}`)
          .then((r) => { if (r.data?.success) setMedData(r.data.data); })
          .catch(() => {});

        axios.get(`/populacao/estados`)
          .then((r) => {
            if (r.data?.success) {
              const estados = (r.data.data || []).filter((e) => e.tipo === "estado");
              const est = estados.find((e) => e.sigla === stateUF);
              if (est) {
                const rank = [...estados].sort((a, b) => b.populacao - a.populacao)
                  .findIndex((e) => e.sigla === stateUF) + 1;
                setPopData({ ...est, rank, total: estados.length });
              }
            }
          })
          .catch(() => {});

        axios.get(`/pib/estados`, { params: { ano: 2021 } })
          .then((r) => {
            if (r.data?.success) {
              const estados = r.data.data || [];
              const est = estados.find((e) => e.uf === stateUF);
              if (est) {
                const rankPib = [...estados].sort((a, b) => b.pibTotal - a.pibTotal)
                  .findIndex((e) => e.uf === stateUF) + 1;
                const rankPibPerCapita = [...estados].sort((a, b) => b.pibPerCapitaMedio - a.pibPerCapitaMedio)
                  .findIndex((e) => e.uf === stateUF) + 1;
                setPibData({ ...est, rankPib, rankPibPerCapita, total: estados.length });
              }
            }
          })
          .catch(() => {});

        const res = await axios.get(`/comex/summary/${selectedYear}`);

        if (!alive) return;

        if (res.data?.success) {
          const stateInfo = res.data.data?.states?.find((s) => s.sg_uf_ncm === stateUF);
          if (stateInfo) {
            setStateData({
              ...stateInfo,
              total_export: parseFloat(stateInfo.total_export) || 0,
              total_import: parseFloat(stateInfo.total_import) || 0,
              balance: parseFloat(stateInfo.balance) || 0,
            });
          } else {
            setStateData(null);
          }
        } else {
          setStateData(null);
        }
      } catch (err) {
        console.error("Erro ao carregar dados do estado", err);
        if (alive) setStateData(null);
      } finally {
        if (alive) setLoading(false);
      }
    };

    if (stateUF) fetchStateData();

    return () => {
      alive = false;
    };
  }, [stateUF, selectedYear]);

  const formatPop = (val) => new Intl.NumberFormat("pt-BR").format(val || 0);

  const formatBRL = (val) => {
    if (!val || val === 0) return "R$ 0";
    if (Math.abs(val) >= 1e9) return `R$ ${(val / 1e9).toFixed(1)} tri`;
    if (Math.abs(val) >= 1e6) return `R$ ${(val / 1e6).toFixed(1)} bi`;
    if (Math.abs(val) >= 1e3) return `R$ ${(val / 1e3).toFixed(1)} mi`;
    return `R$ ${val.toLocaleString("pt-BR", { maximumFractionDigits: 0 })} mil`;
  };

  const formatUSD = (val) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      notation: "compact",
    }).format(val || 0);

  const formatUSDFull = (val) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(val || 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[var(--accent)] mx-auto mb-4" />
          <p className="text-[var(--muted)]">Carregando dados do estado...</p>
        </div>
      </div>
    );
  }

  if (!stateData) {
    return (
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        <Card>
          <div className="text-center py-8">
            <h2 className="text-xl font-semibold text-[var(--text)] mb-4">Estado não encontrado</h2>
            <button
              onClick={() => navigate("/")}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--accent)] text-white rounded-lg hover:opacity-90 transition-opacity"
            >
              <ArrowLeft size={18} />
              Voltar ao Dashboard
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
      <SEO
        title={`${stateUF} - Dados de Comércio Exterior`}
        description={`Exportações, importações e saldo comercial do estado de ${stateUF}, com dados oficiais do Comex Stat.`}
        path={`/estado/${stateSigla}${year ? `/${year}` : ""}`}
      />

      {/* Síntese */}
      {(popData || pibData) && (
        <div className="mb-8">
          <h2 className="text-xl font-bold text-[var(--text)] mb-4">Síntese — {stateUF}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <KPICard
              label="População"
              value={popData ? formatPop(popData.populacao) : "—"}
              change={popData?.rank ? `${popData.rank}º de ${popData.total} estados` : ""}
              changeType="neutral"
              icon={<Users size={20} />}
              className="bg-gradient-to-br from-purple-500 to-purple-600 text-white border-0"
            />
            <KPICard
              label="PIB Total"
              value={pibData ? formatBRL(pibData.pibTotal) : "—"}
              change={pibData?.rankPib ? `${pibData.rankPib}º de ${pibData.total} estados` : ""}
              changeType="neutral"
              icon={<BarChart3 size={20} />}
              className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0"
            />
            <KPICard
              label="PIB per capita"
              value={
                pibData && pibData.pibPerCapitaMedio > 0
                  ? `R$ ${(pibData.pibPerCapitaMedio * 1000).toLocaleString("pt-BR", { maximumFractionDigits: 0 })}`
                  : "—"
              }
              change={pibData?.rankPibPerCapita ? `${pibData.rankPibPerCapita}º de ${pibData.total} estados` : ""}
              changeType="neutral"
              icon={<DollarSign size={20} />}
              className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0"
            />
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[var(--text)] mb-2">{stateUF} - Dados Comerciais</h1>
          <p className="text-[var(--muted)]">Detalhes de comércio exterior</p>
        </div>

        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-[var(--text)]">Ano:</label>

          <select
            value={selectedYear}
            onChange={(e) => navigate(`/estado/${stateUF}/${e.target.value}`)}
            className="px-4 py-2 rounded-lg border border-[var(--border)] bg-[var(--card)] text-[var(--text)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
          >
            {Array.from({ length: 27 }, (_, i) => 2000 + i)
              .reverse()
              .map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
          </select>

          <button
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 px-4 py-2 border border-[var(--border)] rounded-lg hover:bg-[var(--accent-soft)] transition-colors"
          >
            <ArrowLeft size={18} />
            <span className="hidden sm:inline">Voltar</span>
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <KPICard
          label="Exportado"
          value={formatUSD(stateData.total_export)}
          change={formatUSDFull(stateData.total_export)}
          icon={<TrendingUp size={20} />}
          className="bg-gradient-to-br from-green-500 to-green-600 text-white border-0"
        />
        <KPICard
          label="Importado"
          value={formatUSD(stateData.total_import)}
          change={formatUSDFull(stateData.total_import)}
          icon={<TrendingDown size={20} />}
          className="bg-gradient-to-br from-orange-500 to-orange-600 text-white border-0"
        />
        <KPICard
          label="Saldo Comercial"
          value={formatUSD(stateData.balance)}
          change={formatUSDFull(stateData.balance)}
          icon={<DollarSign size={20} />}
          className={`${stateData.balance >= 0 ? "bg-gradient-to-br from-blue-500 to-blue-600" : "bg-gradient-to-br from-red-500 to-red-600"
            } text-white border-0`}
        />
      </div>

      {/* Histórico */}
      <div className="mb-6 min-h-0">
        <StateHistoryChart
          key={`hist-${stateUF}-${selectedYear}`}
          stateSigla={stateUF}
          isMobile={isMobile}
        />
      </div>

      {/* Mapa de Municípios */}
      <div className="min-h-0">
        <StateMunicipalitiesMap
          key={`map-${stateUF}-${selectedYear}`}
          stateSigla={stateUF}
          year={selectedYear}
          isMobile={isMobile}
        />
      </div>

      {/* ===================== Seção de Saúde ===================== */}
      {medData && medData.total > 0 && (
        <div className="mt-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-[var(--text)] mb-2">
              Saúde — {stateUF}
            </h2>
            <p className="text-[var(--muted)]">
              Estabelecimentos de saúde &middot; CNES/DATASUS
            </p>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-6">
            <KPICard
              label="Estabelecimentos"
              value={new Intl.NumberFormat("pt-BR").format(medData.total)}
              icon={<Stethoscope size={20} />}
              className="bg-gradient-to-br from-sky-500 to-sky-600 text-white border-0"
            />
            <KPICard
              label="Municípios"
              value={medData.totalMunicipios}
              icon={<MapPin size={20} />}
              className="bg-gradient-to-br from-cyan-500 to-cyan-600 text-white border-0"
            />
            <KPICard
              label="Serviços"
              value={medData.totalServicos}
              icon={<ClipboardList size={20} />}
              className="bg-gradient-to-br from-teal-500 to-teal-600 text-white border-0"
            />
          </div>

          {/* Municípios + Serviços */}
          <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-2"} gap-6 mb-6`}>
            {/* Top Municípios */}
            <Card title="Estabelecimentos por Município" subtitle="Maiores números de unidades">
              <div className="space-y-1">
                {medData.porMunicipio?.slice(0, 10).map((m, i) => {
                  const maxTotal = medData.porMunicipio[0]?.total || 1;
                  const pct = (m.total / maxTotal) * 100;
                  const nomeCurto = (m.municipio || "").toLowerCase().replace(/(^|\s)\S/g, (l) => l.toUpperCase());
                  const row = (
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-sm text-[var(--text)] truncate">{nomeCurto}</span>
                        <span className="text-xs text-[var(--muted)] ml-2 whitespace-nowrap">{new Intl.NumberFormat("pt-BR").format(m.total)}</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-sky-500 to-cyan-500 transition-all duration-500"
                          style={{ width: `${Math.max(pct, 0.5)}%` }}
                        />
                      </div>
                    </div>
                  );
                  return m.codigoIbge ? (
                    <Link
                      key={m.municipio || i}
                      to={`/municipio/${m.codigoIbge}`}
                      className="flex items-center gap-3 py-1.5 rounded-lg hover:bg-[var(--border)]/20 transition-colors px-1 -mx-1"
                    >
                      <span className="text-xs font-mono text-[var(--muted)] w-6 text-right">{i + 1}.</span>
                      {row}
                    </Link>
                  ) : (
                    <div key={m.municipio || i} className="flex items-center gap-3 py-1.5">
                      <span className="text-xs font-mono text-[var(--muted)] w-6 text-right">{i + 1}.</span>
                      {row}
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Top Serviços */}
            <Card title="Principais Serviços" subtitle="Por número de estabelecimentos">
              <div className="space-y-1">
                {medData.porServico?.slice(0, 10).map((s, i) => {
                  const maxTotal = medData.porServico[0]?.total || 1;
                  const pct = (s.total / maxTotal) * 100;
                  const nomeCurto = (s.servicoNome || "").toLowerCase().replace(/(^|\s)\S/g, (l) => l.toUpperCase());
                  return (
                    <div key={s.servicoCodigo || i} className="flex items-center gap-3 py-1.5">
                      <span className="text-xs font-mono text-[var(--muted)] w-6 text-right">{i + 1}.</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-sm text-[var(--text)] truncate" title={nomeCurto}>{nomeCurto}</span>
                          <span className="text-xs text-[var(--muted)] ml-2 whitespace-nowrap">{new Intl.NumberFormat("pt-BR").format(s.total)}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-400 transition-all duration-500"
                            style={{ width: `${Math.max(pct, 0.5)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* ===================== Seção de Políticos ===================== */}
      {polData && polData.total > 0 && (
        <div className="mt-8">
          <div className="mb-6">
            <h2 className="text-2xl font-bold text-[var(--text)] mb-2">
              Candidatos — {stateUF}
            </h2>
            <p className="text-[var(--muted)]">
              Eleições {polData.ano} &middot; TSE
            </p>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <KPICard
              label="Candidatos"
              value={new Intl.NumberFormat("pt-BR").format(polData.total)}
              icon={<Vote size={20} />}
              className="bg-gradient-to-br from-violet-500 to-violet-600 text-white border-0"
            />
            <KPICard
              label="Partidos"
              value={polData.totalPartidos}
              icon={<DollarSign size={20} />}
              className="bg-gradient-to-br from-fuchsia-500 to-fuchsia-600 text-white border-0"
            />
            {(() => {
              const fem = polData.porGenero?.find(g => g.genero === "FEMININO");
              const pctFem = polData.total > 0 && fem ? ((fem.total / polData.total) * 100).toFixed(1) : "0";
              return (
                <KPICard
                  label="Mulheres"
                  value={`${pctFem}%`}
                  icon={<UserCheck size={20} />}
                  className="bg-gradient-to-br from-pink-500 to-pink-600 text-white border-0"
                />
              );
            })()}
            <KPICard
              label="Prefeitos Eleitos"
              value={polData.prefeitos?.length || 0}
              icon={<Vote size={20} />}
              className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0"
            />
          </div>

          {/* Partidos + Prefeitos */}
          <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-2"} gap-6 mb-6`}>
            {/* Top Partidos */}
            <Card title="Maiores Partidos" subtitle="Por número de candidatos">
              <div className="space-y-1">
                {polData.porPartido?.slice(0, 10).map((p, i) => {
                  const maxTotal = polData.porPartido[0]?.total || 1;
                  const pct = (p.total / maxTotal) * 100;
                  return (
                    <div key={p.partido || i} className="flex items-center gap-3 py-1.5">
                      <span className="text-xs font-mono text-[var(--muted)] w-6 text-right">{i + 1}.</span>
                      <span className="text-xs font-semibold text-[var(--accent)] w-12">{p.partido}</span>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <span className="text-sm text-[var(--text)]">{new Intl.NumberFormat("pt-BR").format(p.total)}</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-[var(--border)] overflow-hidden">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 transition-all duration-500"
                            style={{ width: `${Math.max(pct, 0.5)}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>

            {/* Prefeitos Eleitos */}
            {polData.prefeitos?.length > 0 && (
              <Card title="Prefeitos Eleitos" subtitle={`${polData.prefeitos.length} municípios`}>
                <div className="space-y-1">
                  {(showAllPrefeitos ? polData.prefeitos : polData.prefeitos.slice(0, 10)).map((p, i) => (
                    <Link to={`/politica/candidato/${p.sqCandidato}`} key={p.municipio || i} className="flex items-center gap-3 py-1.5 rounded-lg hover:bg-[var(--border)]/20 transition-colors px-1 -mx-1">
                      <CandidatoAvatar candidato={p} foto={p.foto} nome={p.nome} size={32} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-[var(--text)] truncate">{p.nome}</span>
                          <span className="text-xs text-[var(--accent)] ml-2">{p.partido}</span>
                        </div>
                        <span className="text-xs text-[var(--muted)]">{p.municipio}</span>
                      </div>
                    </Link>
                  ))}
                </div>
                {polData.prefeitos.length > 10 && (
                  <button
                    onClick={() => setShowAllPrefeitos((v) => !v)}
                    className="mt-3 flex items-center gap-1 text-sm text-[var(--accent)] hover:underline mx-auto"
                  >
                    {showAllPrefeitos ? "Mostrar menos" : `Ver todos (${polData.prefeitos.length})`}
                  </button>
                )}
              </Card>
            )}
          </div>

          {/* Gênero + Raça */}
          <div className={`grid ${isMobile ? "grid-cols-1" : "grid-cols-2"} gap-6 mb-6`}>
            <Card title="Gênero" subtitle="Distribuição dos candidatos">
              <div className="space-y-2">
                {polData.porGenero?.filter(g => g.genero && g.genero !== "NÃO DIVULGÁVEL").map((g, i) => {
                  const pct = polData.total > 0 ? (g.total / polData.total) * 100 : 0;
                  const colors = { MASCULINO: "from-blue-500 to-blue-400", FEMININO: "from-pink-500 to-pink-400" };
                  return (
                    <div key={g.genero || i} className="flex items-center gap-3">
                      <span className="text-sm text-[var(--text)] w-20">{g.genero === "MASCULINO" ? "Masculino" : "Feminino"}</span>
                      <div className="flex-1">
                        <div className="h-2.5 rounded-full bg-[var(--border)] overflow-hidden">
                          <div
                            className={`h-full rounded-full bg-gradient-to-r ${colors[g.genero] || "from-gray-400 to-gray-300"} transition-all duration-500`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-xs text-[var(--muted)] w-16 text-right">{pct.toFixed(1)}%</span>
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card title="Cor / Raça" subtitle="Autodeclaração dos candidatos">
              <div className="space-y-2">
                {polData.porRaca?.filter(r => r.raca && r.raca !== "NÃO DIVULGÁVEL" && r.raca !== "SEM INFORMAÇÃO").map((r, i) => {
                  const pct = polData.total > 0 ? (r.total / polData.total) * 100 : 0;
                  const racaColors = {
                    BRANCA: "from-slate-400 to-slate-300", PRETA: "from-gray-700 to-gray-600",
                    PARDA: "from-amber-500 to-amber-400", AMARELA: "from-yellow-400 to-yellow-300",
                    INDÍGENA: "from-red-500 to-red-400",
                  };
                  return (
                    <div key={r.raca || i} className="flex items-center gap-3">
                      <span className="text-sm text-[var(--text)] w-20 capitalize">{(r.raca || "").toLowerCase()}</span>
                      <div className="flex-1">
                        <div className="h-2.5 rounded-full bg-[var(--border)] overflow-hidden">
                          <div
                            className={`h-full rounded-full bg-gradient-to-r ${racaColors[r.raca] || "from-gray-400 to-gray-300"} transition-all duration-500`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                      </div>
                      <span className="text-xs text-[var(--muted)] w-16 text-right">{pct.toFixed(1)}%</span>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatePage;
